'use client'

import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import SubscribeForm from './SubscribeForm'
import { track } from '@/utils/analytics'
import {
  getAnalyticsPreferencesVisible,
  subscribeToAnalyticsPreferencesVisibility,
} from '@/utils/analyticsConsent'

/**
 * Scroll-triggered slide-in.
 *
 * Every design constraint here comes from one fact: this site's traffic is
 * almost entirely organic search, so the highest-converting format in the
 * research — the full-screen interstitial — is the one format that could cost
 * us rankings. Google's intrusive-interstitial guidance targets exactly that
 * pattern on mobile.
 *
 * So instead:
 *   · A corner card on desktop, a bottom sheet on mobile. Never full screen,
 *     and comfortably under the ~30% of viewport the guidance is concerned
 *     with.
 *   · Fires at 55% scroll depth, never on load and never on a timer alone.
 *     Someone who has read half a page has demonstrated interest; someone who
 *     landed 800ms ago has not.
 *   · Dismissal is remembered for 90 days, and a successful signup is
 *     remembered permanently. Being asked again after subscribing is the
 *     fastest way to make someone unsubscribe.
 *   · Never on the pages where a contextual form already exists — being asked
 *     twice on one screen reads as desperation and suppresses both.
 *   · Escape closes it, focus is not stolen, and it is polite="off" so it
 *     never interrupts a screen reader mid-sentence.
 */

const DISMISS_KEY = 'mp_news_dismissed_at'
// Keep the legacy storage key so a prior acknowledgement is not forgotten,
// but do not treat a generic API acknowledgement as proof of membership.
const REQUESTED_KEY = 'mp_news_subscribed'
const DISMISS_DAYS = 90
const SCROLL_TRIGGER = 0.55

/** Pages that already ask, in a better-targeted way. Do not double-ask. */
const SUPPRESSED_PREFIXES = ['/check', '/newsletter', '/contact', '/privacy', '/terms', '/delete-account', '/unsubscribe']

export default function ScrollSubscribe({ pathname }: { pathname: string }) {
  const [visible, setVisible] = useState(false)
  const [closing, setClosing] = useState(false)
  const firedRef = useRef(false)
  const submittedRef = useRef(false)
  const analyticsPreferencesVisible = useSyncExternalStore(
    subscribeToAnalyticsPreferencesVisibility,
    getAnalyticsPreferencesVisible,
    () => false
  )

  const suppressed = SUPPRESSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))

  const close = useCallback(() => {
    setClosing(true)
    if (!submittedRef.current) {
      track('email_capture_dismiss', {
        cta_surface: 'newsletter_scroll_prompt',
        offer_id: 'monthly_evidence_brief_v2',
      })
    }
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      // Private mode. Losing the preference is survivable; throwing is not.
    }
    // Let the transition finish before unmounting.
    window.setTimeout(() => setVisible(false), 220)
  }, [])

  const rememberRequested = useCallback(() => {
    submittedRef.current = true
    try {
      localStorage.setItem(REQUESTED_KEY, '1')
    } catch {
      // The acknowledgement still remains visible for this page visit.
    }
    // Keep the live success region mounted until the visitor dismisses it.
    // Announcement timing varies by assistive technology, so a short timeout
    // would not be a reliable accessibility contract.
  }, [])

  useEffect(() => {
    if (suppressed) return

    try {
      if (localStorage.getItem(REQUESTED_KEY)) return
      const dismissed = Number(localStorage.getItem(DISMISS_KEY) ?? 0)
      if (dismissed && Date.now() - dismissed < DISMISS_DAYS * 864e5) return
    } catch {
      // No storage access — fall through and show it. One prompt is better
      // than a broken component.
    }

    // Respect the same reduced-motion signal the rest of the site honours by
    // skipping the slide entirely, not the prompt.
    const onScroll = () => {
      if (firedRef.current) return
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if (window.scrollY / scrollable < SCROLL_TRIGGER) return

      firedRef.current = true
      setVisible(true)
      window.removeEventListener('scroll', onScroll)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [pathname, suppressed])

  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible, close])

  // Never stack two unrelated asks at the bottom of the viewport. Keep the
  // newsletter's state while privacy preferences are open, then restore it if
  // it was already visible after the visitor finishes that choice.
  if (!visible || suppressed || analyticsPreferencesVisible) return null

  return (
    <div
      // aria-live off and role="complementary": this is an offer, not an
      // alert, and it must not interrupt whatever is being read.
      role="complementary"
      aria-label="Newsletter signup"
      className={`fixed z-40 transition-[opacity,transform] duration-200 ease-out
        inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[22rem]
        ${closing ? 'translate-y-3 opacity-0' : 'translate-y-0 opacity-100'}`}
    >
      <div className="relative rounded-t-2xl border border-ink/10 bg-white p-5 shadow-2xl pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-2xl sm:pb-5">
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss newsletter signup"
          className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-ink/[0.05] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-forest"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className="pr-8 text-xs font-semibold uppercase tracking-[0.2em] text-forest">
          Before you go
        </p>
        <p className="mt-1.5 font-display text-base font-bold leading-snug text-ink">
          Know what changed—and what to swap next.
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
          Corrected scores, newly reviewed products, and one practical lower-contact
          swap. Roughly monthly.
        </p>

        <div className="mt-3.5">
          <SubscribeForm
            source="scroll_prompt"
            context={pathname}
            variant="compact"
            cta="Send it"
            offerId="monthly_evidence_brief_v2"
            onSuccess={rememberRequested}
          />
        </div>
      </div>
    </div>
  )
}
